import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, shareReplay, takeUntil } from 'rxjs/operators';
import { Directive, OnDestroy } from '@angular/core';
import { Duration } from 'moment';
import { IDataLoadMetadata } from './models/i-data-load-metadata';
import { IRunnerOptions } from './models/i-runner.options';
import { RecurringTaskAction } from './models/recurring-task.action';
import { FetchStrategyResolver } from './fetch-strategies/fetch-strategy.resolver';
import { FetchStrategy } from './fetch-strategies/fetch-strategy.enum';

export function defaultGetPeriodicTaskKey(params) {
  if (params === undefined) {
    return 'default';
  }

  if (typeof params === 'object') {
    if (!params) {
      return 'default';
    }

    return JSON.stringify(
      Object.keys(params)
        .sort()
        .reduce((agg, key) => {
          agg[key] = params[key];
          return agg;
        }, {}),
    );
  }

  if (typeof params === 'string') {
    return params;
  }

  return params.toString();
}

@Directive()
export class RecurringTaskRunner<TParameters, TData = void> implements OnDestroy {
  public constructor(
    private readonly runnerStrategyResolver: FetchStrategyResolver,
    private readonly fetchData: (parameters: TParameters, intervalId: number, preload: boolean) => Observable<TData>,
    runnerOptions: IRunnerOptions<TParameters>,
  ) {
    this.isDestroyed = false;
    this.dataLoadMetadatas = {};
    this.getPeriodicTaskKey = runnerOptions.getPeriodicTaskKey || RecurringTaskRunner.defaultGetPeriodicTaskKey;
    if (runnerOptions.canBeResumedAndPaused) {
      this.action$ = new BehaviorSubject<RecurringTaskAction>(RecurringTaskAction.Resume);
    }
    this.runnerStrategy = runnerOptions.strategy || FetchStrategy.Autofetch;
  }

  private static defaultGetPeriodicTaskKey: (params: any) => string = defaultGetPeriodicTaskKey;
  private isDestroyed: boolean;
  private readonly stopPeriodicRequests$: Subject<void> = new Subject<void>();
  private readonly dataLoadMetadatas: {
    [key: string]: IDataLoadMetadata<TData>;
  };
  private readonly action$: BehaviorSubject<RecurringTaskAction>;
  private readonly getPeriodicTaskKey: (params: TParameters) => string;
  private readonly runnerStrategy: FetchStrategy;

  /*
   * starts featching data with returning data and stops fetching when unsubscribed
   */
  public start$(params: TParameters, interval: Duration, firstInterval?: Duration): Observable<TData> {
    return new Observable<TData>((observer$) => {
      const subscription$ = this.startFetch(params, interval, firstInterval).subscribe(
        (data) => observer$.next(data),
        (error) => observer$.error(error),
        () => observer$.complete(),
      );

      return () => {
        this.stop(params);
        subscription$.unsubscribe();
      };
    });
  }

  /*
   * starts featching data with returning data
   */
  public start(params: TParameters, interval: Duration, firstInterval?: Duration): Observable<TData> {
    return this.startFetch(params, interval, firstInterval);
  }

  public stop(params: TParameters): void {
    this.failIfDestroyed();

    const metadata = this.getLoadDataMetadata(params);

    if (metadata) {
      metadata.loadStartedTimes--;
      if (metadata.loadStartedTimes <= 0) {
        this.stopPeriodicRequests$.next();
        this.removeLoadDataMetadata(metadata);
      }
    }
  }

  public pauseAll(): void {
    this.failIfDestroyed();
    if (!this.action$) {
      throw new Error("This Runner can't be paused.");
    }

    this.action$.next(RecurringTaskAction.Pause);
  }

  public cancelAndPauseAll(): void {
    this.failIfDestroyed();
    if (!this.action$) {
      throw new Error("This Runner can't be paused.");
    }

    this.action$.next(RecurringTaskAction.CancelAndPause);
  }

  public resumeAll(): void {
    this.failIfDestroyed();
    if (!this.action$) {
      throw new Error("This Runner can't be resumed.");
    }

    this.action$.next(RecurringTaskAction.Resume);
  }

  public execute(): void {
    this.failIfDestroyed();
    if (!this.action$) {
      throw new Error("This Runner can't be resumed.");
    }

    this.action$.next(RecurringTaskAction.Execute);
  }

  public ngOnDestroy(): void {
    for (const key of Object.keys(this.dataLoadMetadatas)) {
      const metadata = this.dataLoadMetadatas[key];
      metadata.loadStartedTimes = 0;

      delete this.dataLoadMetadatas[key];
    }

    this.stopPeriodicRequests$.next();
    this.stopPeriodicRequests$.complete();

    if (this.action$) {
      this.action$.complete();
    }
  }

  private startFetch(params: TParameters, interval: Duration, firstInterval?: Duration): Observable<TData> {
    this.failIfDestroyed();

    const metadata = this.getOrCreateLoadDataMetadata(params, interval, firstInterval);

    metadata.loadStartedTimes++;

    return metadata.loadData$;
  }

  private failIfDestroyed(): void {
    if (this.isDestroyed) {
      throw new Error('The object RecurringTaskRunner is destroyed.');
    }
  }

  private getLoadDataMetadata(parameters: TParameters): IDataLoadMetadata<TData> {
    const key = this.getPeriodicTaskKey(parameters);

    return this.dataLoadMetadatas[key];
  }

  private removeLoadDataMetadata(metadata: IDataLoadMetadata<TData>) {
    const key = metadata.key;
    metadata.key = null;
    metadata.interval = null;
    metadata.loadData$ = null;
    metadata.loadStartedTimes = null;

    delete this.dataLoadMetadatas[key];
  }

  private getOrCreateLoadDataMetadata(
    parameters: TParameters,
    intervalDuration: Duration,
    firstIntervalDuration?: Duration,
  ): IDataLoadMetadata<TData> {
    const interval = intervalDuration.asMilliseconds();
    const firstInterval = firstIntervalDuration ? firstIntervalDuration.asMilliseconds() : null;

    const key = this.getPeriodicTaskKey(parameters);

    let loadDataMetadata = this.dataLoadMetadatas[key];
    if (loadDataMetadata) {
      if (loadDataMetadata.interval !== interval) {
        throw new Error(
          `The periodic task with parameters ${key} is already running and have interval ${loadDataMetadata.interval} but there is attempt to start this task with other interval ${interval}`,
        );
      }
    } else {
      loadDataMetadata = this.dataLoadMetadatas[key] = {
        key,
        loadData$: this.runnerStrategyResolver
          .getStrategy(this.runnerStrategy)
          .startRecurringTask(this.fetchData, parameters, interval, firstInterval, this.action$)
          .pipe(takeUntil(this.stopPeriodicRequests$.pipe(filter(() => loadDataMetadata.loadStartedTimes <= 0))), shareReplay(1)),
        interval,
        loadStartedTimes: 0,
      };
    }

    return loadDataMetadata;
  }
}
